<template>
  <div class="report-table-scroll">
    <table class="report-t">
      <thead>
        <tr>
          <th
            v-for="col in columns"
            :key="col.key"
            :class="[
              col.numeric ? 'report-t-num' : '',
              col.sortable ? 'report-t-sortable' : '',
              col.sortable && sortKey === col.key && sortDir === 1 ? 'report-t-sort--asc' : '',
              col.sortable && sortKey === col.key && sortDir === -1 ? 'report-t-sort--desc' : ''
            ]"
            @click="col.sortable ? $emit('sort', col.key) : null"
          >
            {{ col.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in sortedRows" :key="row[rowKey]">
          <td
            v-for="col in columns"
            :key="col.key"
            :class="[
              col.numeric ? 'report-t-num' : '',
              getCellClass(row, col)
            ]"
          >
            {{ formatCellValue(row, col) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: 'ReportTable',
  props: {
    columns: {
      type: Array,
      required: true,
      validator: (cols) => cols.every(col => col.key && col.label !== undefined)
    },
    rows: {
      type: Array,
      required: true
    },
    sortKey: {
      type: String,
      default: null
    },
    sortDir: {
      type: Number,
      default: 1,
      validator: (val) => val === 1 || val === -1
    },
    rowKey: {
      type: String,
      required: true
    },
    formatCell: {
      type: Function,
      default: null
    }
  },
  emits: ['sort'],
  computed: {
    sortedRows() {
      if (!this.sortKey) return this.rows;
      return [...this.rows].sort((a, b) => {
        const va = a[this.sortKey];
        const vb = b[this.sortKey];
        if (typeof va === 'string' && typeof vb === 'string') {
          return this.sortDir * va.localeCompare(vb);
        }
        return this.sortDir * (Number(va) - Number(vb));
      });
    }
  },
  methods: {
    formatCellValue(row, col) {
      if (this.formatCell) {
        return this.formatCell(row, col);
      }
      return row[col.key];
    },
    getCellClass(row, col) {
      if (col.cellClass) {
        return col.cellClass(row);
      }
      return '';
    }
  }
};
</script>

<style scoped>
.report-table-scroll {
  overflow-x: auto;
}

.report-t {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.report-t thead tr {
  background: var(--color-bg);
}

.report-t th {
  padding: var(--space-sm) var(--space-md);
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border);
}

.report-t th.report-t-num {
  text-align: right;
}

.report-t-sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}

.report-t-sortable:hover {
  color: var(--color-text);
}

.report-t-sortable.report-t-sort--asc::after {
  content: ' ▲';
  font-size: 0.65em;
  opacity: 0.8;
}

.report-t-sortable.report-t-sort--desc::after {
  content: ' ▼';
  font-size: 0.65em;
  opacity: 0.8;
}

.report-t td {
  padding: var(--space-sm) var(--space-md);
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
}

.report-t tbody tr:last-child td {
  border-bottom: none;
}

.report-t tbody tr:hover {
  background: var(--color-bg);
}

.report-t-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.report-t-ok {
  color: #2e7d32;
  font-weight: 600;
}

.report-t-warn {
  color: var(--color-error);
  font-weight: 600;
}
</style>
